import { jsonApiOptions } from '@efdigital/ll-admin-v2/lib/json-api';
import { cacheKeyFor, type Store } from '@warp-drive/core';
import type {
  CollectionRelationship,
  ResourceRelationship,
} from '@warp-drive/core/types/cache/relationship';
import type { ResourceKey } from '@warp-drive/core/types/identifier';
import {
  createRecord,
  serializePatch,
  updateRecord,
} from '@warp-drive/utilities/json-api';

/**
 * Represents an error that occurred while saving a record.
 */
export interface SaveError {
  recordType: string;
  recordId: string | null;
  error: unknown;
}

/**
 * Options for controlling the behavior of deepCreate and deepUpdate.
 */
export interface DeepSaveOptions {
  /**
   * Maximum depth to traverse relationships. Prevents infinite recursion in deeply nested structures.
   * Default: Infinity
   */
  maxDepth?: number;

  /**
   * Internal tracking of visited records to prevent circular reference loops.
   * Should not be set by external callers.
   * @internal
   */
  visited?: Set<string>;

  /**
   * Internal collection of errors that occurred during save operations.
   * Should not be set by external callers.
   * @internal
   */
  errors?: SaveError[];
}

/**
 * An aggregate error thrown when one or more records fail to save during a deep save operation.
 */
export class DeepSaveError extends Error {
  readonly errors: SaveError[];

  constructor(errors: SaveError[]) {
    const count = errors.length;
    const plural = count === 1 ? 'record' : 'records';
    super(`Failed to save ${count} ${plural} during deep save operation`);
    this.name = 'DeepSaveError';
    this.errors = errors;
  }
}

/**
 * Creates a unique identifier for a record based on its cache key.
 */
function getCacheKeyId(cacheKey: ResourceKey): string {
  return `${cacheKey.type}:${cacheKey.id ?? 'null'}`;
}

/**
 * Deeply saves a record and all its related dirty records.
 *
 * This function:
 * 1. Traverses all relationships
 * 2. Recursively saves related records (children first)
 * 3. Creates new records or updates existing records based on their state
 * 4. Collects any errors that occur during saves
 *
 * @param record - The record to save
 * @param store - The Warp Drive store instance
 * @param options - Optional configuration for save behavior
 *
 * @example
 * ```typescript
 * // Simple save
 * await deepSave(cohort, this.store);
 *
 * // Limit depth to avoid deep traversal
 * await deepSave(cohort, this.store, { maxDepth: 2 });
 * ```
 */
export async function deepSave(
  record: unknown,
  store: Store,
  options: DeepSaveOptions = {}
): Promise<void> {
  const {
    maxDepth = Infinity,
    visited = new Set<string>(),
    errors = [],
  } = options;

  // Early return if we've reached max depth (depth 0 means we've exhausted our limit)
  if (maxDepth <= 0) {
    return;
  }

  // Get the cache key for this record
  const cacheKey = cacheKeyFor(record);
  const cacheKeyId = getCacheKeyId(cacheKey);

  // Prevent circular references
  if (visited.has(cacheKeyId)) {
    return;
  }
  visited.add(cacheKeyId);

  // Get all fields from the schema
  const fields = store.schema.fields(cacheKey);

  // Process each relationship
  for (const [fieldName, field] of fields.entries()) {
    // Only process relationship fields
    if (field.kind !== 'belongsTo' && field.kind !== 'hasMany') {
      continue;
    }

    // Collect all related record keys
    const relatedKeys = new Set<ResourceKey>();

    if (field.kind === 'hasMany') {
      const relationship = store.cache.getRelationship(
        cacheKey,
        fieldName,
        true
      ) as CollectionRelationship;
      if (relationship.data) {
        for (const relatedItemKey of relationship.data) {
          // Add all records for traversal to find dirty records nested deeper
          relatedKeys.add(relatedItemKey);
        }
      }
    } else if (field.kind === 'belongsTo') {
      const relationship = store.cache.getRelationship(
        cacheKey,
        fieldName,
        false
      ) as ResourceRelationship;
      if (relationship.data) {
        const relatedItemKey = relationship.data;
        // Add all records for traversal to find dirty records nested deeper
        relatedKeys.add(relatedItemKey);
      }
    }

    // Recursively save each related record (children first)
    for (const relatedKey of relatedKeys) {
      try {
        const relatedRecord = store.peekRecord(relatedKey);
        if (relatedRecord) {
          await deepSave(relatedRecord, store, {
            maxDepth: maxDepth - 1,
            visited,
            errors,
          });
        }
      } catch (error) {
        // If we can't peek the record, it may have been unloaded
        // Continue processing other records
        console.warn(
          `Could not peek record ${relatedKey.type}:${relatedKey.id}`,
          error
        );
      }
    }
  }

  // Determine if this is a new record or existing record
  const isNewRecord =
    typeof record === 'object' &&
    record !== null &&
    'isNew' in record &&
    record.isNew === true;

  // Check if this record has unsaved changes
  const changedAttrs = store.cache.changedAttrs(cacheKey);
  const changedRelationships = store.cache.changedRelationships(cacheKey);
  const hasChangedAttrs = changedAttrs && Object.keys(changedAttrs).length > 0;
  const hasChangedRelationships = changedRelationships.size > 0;

  // Only save if the record has changes or is new
  if (isNewRecord || hasChangedAttrs || hasChangedRelationships) {
    try {
      if (isNewRecord) {
        // Create new record
        const init = createRecord(record, {
          ...jsonApiOptions,
        });

        init.body = JSON.stringify({
          data: structuredClone(store.cache.peek(cacheKeyFor(record))),
        });

        init.headers.set('Content-Type', 'application/vnd.api+json');

        await store.request(init);
      } else {
        // Update existing record
        const init = updateRecord(record, {
          ...jsonApiOptions,
          patch: true,
        });

        init.body = JSON.stringify(
          serializePatch(store.cache, cacheKeyFor(record))
        );

        init.headers.set('Content-Type', 'application/vnd.api+json');

        await store.request(init);
      }
    } catch (error) {
      console.warn(`deepSave ${cacheKeyId} error`, error);
      errors.push({
        recordType: cacheKey.type,
        recordId: cacheKey.id ?? null,
        error,
      });
    }
  }

  // After completing traversal at root level, throw if any errors occurred
  if (options.errors === undefined && errors.length > 0) {
    throw new DeepSaveError(errors);
  }
}
